/**
 * RoasWidget - Виджет ROAS (Return on Ad Spend) по кампаниям
 * ============================================================
 * 
 * Показывает графики ROAS по дням для каждой кампании
 * с возможностью сравнения между кампаниями
 * 
 * Формула: ROAS = (Накопительный доход / Накопительные затраты) * 100
 */

import React, { useState, useMemo } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useFilters } from '../../hooks/useFilters';
import { useDashboard } from '../../contexts/DashboardContext';
import { MetricsService } from '../../services/metricsService';
import { formatChartDate, formatTooltipDate, getXAxisConfig } from '../../utils/chartUtils';
import { CampaignIcons } from '../ui/brand-icons';
import { Card, CardContent } from '../ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { 
  TrendingUp, 
  DollarSign,
  Target,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  MousePointerClick,
  Goal
} from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { addDays, format, differenceInDays } from 'date-fns';
import { roas as roasSpecs } from '@ggstat/chart-specs';

// Цвета для графиков кампаний — общие с картинкой MCP (shared/chart-specs/specs/roas.json)
const CAMPAIGN_COLORS = roasSpecs.campaignColors;

const ROAS_Y_MAX_STORAGE_KEY = 'roasChartYMax';
const DEFAULT_ROAS_Y_MAX = 200;
const ROAS_MIN_INSTALLS_STORAGE_KEY = 'roasMinInstalls';
const DEFAULT_ROAS_MIN_INSTALLS = 100;

function readStoredNumber(key, fallback) {
  const raw = localStorage.getItem(key);
  const parsed = Number(raw);
  return raw !== null && Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

function NumberSetting({ id, label, value, onChange, step }) {
  return (
    <div className="flex items-center gap-2">
      <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
      <Input
        id={id}
        type="number"
        min={0}
        step={step}
        value={value}
        onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
        className="h-8 w-24 text-sm"
      />
    </div>
  );
}

/**
 * Компонент статистики
 */
function StatCard({ icon: Icon, label, value, subtitle, color = 'primary' }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 bg-${color}/10 rounded-lg`}>
            <Icon className={`h-5 w-5 text-${color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-xl font-bold leading-tight tabular-nums break-words">{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Компонент графика ROAS
 */
function RoasChart({ chartData, campaigns, visibleCampaigns, totals, cohortEndDate, campaignColors, campaignMetadata, yAxisMax }) {
  // Получаем настройки оси X (используем 'day' так как данные по дням)
  const xAxisConfig = getXAxisConfig('day', chartData.length);
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      const sortedPayload = payload;
      
      // Форматируем дату для tooltip
      const formattedLabel = formatTooltipDate(label, 'day');
      
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium mb-2">{formattedLabel}</p>
          <div className="space-y-1">
            {sortedPayload.map((entry, index) => {
              const campaignName = entry.dataKey;
              const campaignTotals = totals[campaignName];
              const metadata = campaignMetadata?.[campaignName];
              
              return (
                <div key={index} className="text-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <CampaignIcons 
                      source={metadata?.source} 
                      platform={metadata?.platform}
                      size={14}
                      gap={1}
                    />
                    <p style={{ color: entry.color }} className="font-medium">
                      {campaignName}
                    </p>
                  </div>
                  <p>
                    <span className="text-muted-foreground">ROAS:</span>{' '}
                    <span className={`font-bold ${entry.value >= 100 ? 'text-green-600' : 'text-amber-600'}`}>
                      {entry.value.toFixed(2)}%
                    </span>
                  </p>
                  {campaignTotals && (
                    <>
                      <p className="text-xs text-muted-foreground">
                        Cost: {MetricsService.formatCurrency(campaignTotals.totalCost)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Revenue: {MetricsService.formatCurrency(campaignTotals.totalRevenue)}
                      </p>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };
  
  return (
    <ResponsiveContainer width="100%" height={500}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis 
          dataKey="date"
          tickFormatter={(value) => formatChartDate(value, 'day')}
          {...xAxisConfig}
        />
        <YAxis 
          className="text-xs"
          tick={{ fill: 'currentColor' }}
          tickFormatter={(value) => `${value}%`}
          domain={[0, yAxisMax > 0 ? yAxisMax : 'auto']}
          allowDataOverflow={yAxisMax > 0}
          label={{ value: 'ROAS (%)', angle: -90, position: 'insideLeft' }}
        />
        <Tooltip content={<CustomTooltip />} />
        
        {/* Вертикальная линия границы выбранного диапазона */}
        {cohortEndDate && (
          <ReferenceLine 
            x={cohortEndDate} 
            stroke="#f59e0b" 
            strokeDasharray="5 5"
            label={{ 
              value: 'Конец периода →', 
              position: 'top',
              fill: '#f59e0b',
              fontSize: 12
            }}
          />
        )}
        
        {/* Линия окупаемости (100%) */}
        <ReferenceLine 
          y={100} 
          stroke="#22c55e" 
          strokeDasharray="5 5"
          label={{ 
            value: 'Окупаемость (100%)', 
            position: 'right',
            fill: '#22c55e',
            fontSize: 12
          }}
        />
        
        {/* Линии для каждой кампании */}
        {campaigns.map((campaign) => {
          if (!visibleCampaigns[campaign]) return null;
          
          return (
            <Line
              key={campaign}
              type="monotone"
              dataKey={campaign}
              stroke={campaignColors[campaign]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          );
        })}
      </LineChart>
    </ResponsiveContainer>
  );
}

/**
 * Компонент таблицы итогов по кампаниям
 */
function CampaignsTotalsTable({ campaigns, totals, visibleCampaigns, onToggleCampaign, campaignColors, campaignMetadata }) {
  // Разделяем кампании на активные (с затратами) и нулевые (без затрат)
  // Сортируем активные кампании по финальному ROAS от большего к меньшему
  const activeCampaigns = campaigns
    .filter(c => (totals[c]?.totalCost || 0) > 0)
    .sort((a, b) => (totals[b]?.finalRoas || 0) - (totals[a]?.finalRoas || 0));
  const zeroCostCampaigns = campaigns.filter(c => (totals[c]?.totalCost || 0) === 0);
  
  // Состояние раскрытия секции нулевых кампаний
  const [showZeroCost, setShowZeroCost] = useState(false);
  
  // Проверяем что все активные кампании видимы
  const allActiveVisible = activeCampaigns.length > 0 && activeCampaigns.every(c => visibleCampaigns[c] === true);
  
  // Рендер строки кампании
  const renderCampaignRow = (campaign, isZeroCost = false) => {
    const campaignTotals = totals[campaign];
    const color = campaignColors[campaign];
    // Показываем день окупаемости только если финальный ROAS >= 100%
    const breakevenDay = (campaignTotals?.finalRoas >= 100) ? campaignTotals?.breakevenDay : null;
    
    return (
      <tr key={campaign} className={`border-t hover:bg-muted/50 ${isZeroCost ? 'bg-muted/20' : ''}`}>
        <td className="py-2 px-2">
          <Checkbox 
            checked={visibleCampaigns[campaign] === true}
            onCheckedChange={(checked) => onToggleCampaign(campaign, checked)}
          />
        </td>
        <td className="py-2 px-2">
          <div className="flex items-center gap-2">
            <CampaignIcons 
              source={campaignMetadata?.[campaign]?.source} 
              platform={campaignMetadata?.[campaign]?.platform}
              size={16}
              gap={2}
            />
            <span 
              className={`font-medium cursor-pointer hover:opacity-70 transition-opacity ${isZeroCost ? 'text-muted-foreground' : ''}`}
              style={{ color: isZeroCost ? undefined : color }}
              onClick={() => onToggleCampaign(campaign, !visibleCampaigns[campaign])}
            >
              {campaign}
            </span>
          </div>
        </td>
        <td className="text-right py-2 px-2 font-medium">
          {MetricsService.formatCurrency(campaignTotals?.totalCost || 0)}
        </td>
        <td className="text-right py-2 px-2 font-medium">
          {MetricsService.formatCurrency(campaignTotals?.totalRevenue || 0)}
        </td>
        <td className="text-right py-2 px-2">
          <span className={`font-bold ${
            (campaignTotals?.finalRoas || 0) >= 100 
              ? 'text-green-600' 
              : 'text-amber-600'
          }`}>
            {(campaignTotals?.finalRoas || 0).toFixed(2)}%
          </span>
        </td>
        <td className="text-center py-2 px-2">
          {breakevenDay !== null ? (
            <span className="text-green-600 font-medium">
              День {breakevenDay}
            </span>
          ) : (
            <span className="text-muted-foreground">
              Не достигнута
            </span>
          )}
        </td>
      </tr>
    );
  };
  
  return (
    <Card>
      <CardContent className="p-4">
        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 [&_th]:bg-card [&_th]:shadow-[inset_0_-1px_0_hsl(var(--border))]">
              <tr className="border-b">
                <th className="text-left py-2 px-2 font-medium text-muted-foreground">
                  <Checkbox 
                    checked={allActiveVisible}
                    onCheckedChange={(checked) => {
                      activeCampaigns.forEach(campaign => onToggleCampaign(campaign, checked));
                    }}
                  />
                </th>
                <th className="text-left py-2 px-2 font-medium text-muted-foreground">Кампания</th>
                <th className="text-right py-2 px-2 font-medium text-muted-foreground">Затраты</th>
                <th className="text-right py-2 px-2 font-medium text-muted-foreground">Доход</th>
                <th className="text-right py-2 px-2 font-medium text-muted-foreground">Финальный ROAS</th>
                <th className="text-center py-2 px-2 font-medium text-muted-foreground">Окупаемость</th>
              </tr>
            </thead>
            <tbody>
              {/* Активные кампании с затратами */}
              {activeCampaigns.map((campaign) => renderCampaignRow(campaign, false))}
              
              {/* Секция нулевых кампаний */}
              {zeroCostCampaigns.length > 0 && (
                <>
                  <tr className="border-t bg-muted/30">
                    <td colSpan="6" className="py-2 px-2">
                      <button
                        onClick={() => setShowZeroCost(!showZeroCost)}
                        className="flex items-center gap-2 w-full text-left hover:text-primary transition-colors"
                      >
                        {showZeroCost ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="text-xs font-medium text-muted-foreground">
                          Кампании без затрат ({zeroCostCampaigns.length})
                        </span>
                      </button>
                    </td>
                  </tr>
                  {showZeroCost && zeroCostCampaigns.map((campaign) => 
                    renderCampaignRow(campaign, true)
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * cumulativeRevenue прореженной кривой на день `day`: линейная интерполяция
 * между соседними точками, после последней точки — её значение, до первой — undefined.
 */
export function interpolateCumulativeRevenue(curve, day) {
  let prev;
  for (const p of curve) {
    if (p.dayFromInstall === day) return p.cumulativeRevenue;
    if (p.dayFromInstall > day) {
      if (!prev) return undefined;
      const span = p.dayFromInstall - prev.dayFromInstall;
      const share = (day - prev.dayFromInstall) / span;
      return prev.cumulativeRevenue + (p.cumulativeRevenue - prev.cumulativeRevenue) * share;
    }
    prev = p;
  }
  return prev ? prev.cumulativeRevenue : undefined;
}

/**
 * Основной компонент виджета
 */
export function RoasWidget() {
  const { platform, dateRange } = useFilters();
  const { includeOrganic } = useDashboard();
  
  // Минимум инсталлов по кампании; кампании ниже порога не попадают ни в график, ни в итоги
  const [minInstalls, setMinInstalls] = useState(() => readStoredNumber(ROAS_MIN_INSTALLS_STORAGE_KEY, DEFAULT_ROAS_MIN_INSTALLS));
  const handleMinInstallsChange = (next) => {
    setMinInstalls(next);
    localStorage.setItem(ROAS_MIN_INSTALLS_STORAGE_KEY, String(next));
  };

  // Читаем состояние чекбокса из localStorage
  const [shouldApplyFees, setShouldApplyFees] = useState(() => {
    const stored = localStorage.getItem('applyPlatformFees');
    return stored === 'true';
  });
  
  // Слушаем изменения в localStorage (когда чекбокс меняется в Sidebar)
  React.useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('applyPlatformFees');
      setShouldApplyFees(stored === 'true');
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('platformFeesChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('platformFeesChanged', handleStorageChange);
    };
  }, []);
  
  // Загружаем данные используя хук
  const widgetData = useWidgetData(
    async (filters, options) => {
      const response = await MetricsService.getRoasData(filters, undefined, undefined, includeOrganic, options);
      return response;
    },
    [includeOrganic]
  );
  
  // Используем хелпер для BaseWidget
  const widgetProps = useBaseWidget(widgetData);
  const { data: rawData } = widgetProps;

  // Мемоизированная обработка данных из Supabase
  const processedData = useMemo(() => {
    if (!rawData || !rawData.cohorts) {
      return {
        chartData: [],
        totals: {},
        campaigns: [],
        campaignMetadata: {},
        summary: {},
        cohortDateRange: {},
        hiddenCampaignsCount: 0
      };
    }

    // 0. Отсекаем кампании с числом инсталлов ниже порога
    const installsByCampaign = {};
    rawData.cohorts.forEach(cohort => {
      installsByCampaign[cohort.campaign] = (installsByCampaign[cohort.campaign] || 0) + (cohort.cohortSize || 0);
    });
    const eligibleCohorts = rawData.cohorts.filter(cohort => installsByCampaign[cohort.campaign] >= minInstalls);
    const hiddenCampaignsCount = Object.values(installsByCampaign).filter(n => n < minInstalls).length;

    // 1. Применяем platform fees к доходам когорт
    const cohortsWithFees = shouldApplyFees
      ? eligibleCohorts.map(cohort => {
          const newRoasCurve = cohort.roasCurve.map(point => {
            const { breakdown } = point;
            
            const inappsAndroid = [{ revenue: breakdown.inappsAndroid || 0 }];
            const inappsIos = [{ revenue: breakdown.inappsIos || 0 }];

            const adjustedAndroid = (platform === 'android' || platform === 'all')
              ? MetricsService.applyPlatformFees(inappsAndroid, true, 'android')
              : inappsAndroid;
            
            const adjustedIos = (platform === 'ios' || platform === 'all')
              ? MetricsService.applyPlatformFees(inappsIos, true, 'ios')
              : inappsIos;

            const adjustedRevenue = 
              (adjustedAndroid[0].revenue) +
              (adjustedIos[0].revenue) +
              (breakdown.admonAndroid || 0) +
              (breakdown.admonIos || 0);
            
            const adjustedRoas = cohort.initialCost > 0 ? (adjustedRevenue / cohort.initialCost) * 100 : 0;

            return { ...point, cumulativeRevenue: adjustedRevenue, cumulativeRoas: adjustedRoas };
          });
          return { ...cohort, roasCurve: newRoasCurve };
        })
      : eligibleCohorts;

    // 2. Собираем все уникальные календарные даты и кампании
    const allDates = new Set();
    const campaigns = new Set();
    const campaignMetadata = {};

    cohortsWithFees.forEach(cohort => {
      campaigns.add(cohort.campaign);
      if (!campaignMetadata[cohort.campaign]) {
        campaignMetadata[cohort.campaign] = cohort.metadata;
      }
      cohort.roasCurve.forEach(point => {
        const date = format(addDays(new Date(cohort.cohortDate), point.dayFromInstall), 'yyyy-MM-dd');
        allDates.add(date);
      });
    });

    const sortedDates = Array.from(allDates).sort();
    const campaignList = Array.from(campaigns);

    // 3. Для каждой даты и кампании вычисляем накопительные значения
    const isSparseCurve = rawData.curveResolution === 'keyDays';
    const dataByDate = {};
    sortedDates.forEach(dateStr => {
      dataByDate[dateStr] = { date: dateStr };
      
      campaignList.forEach(campaign => {
        let totalCost = 0;
        let totalRevenue = 0;

        cohortsWithFees
          .filter(c => c.campaign === campaign)
          .forEach(cohort => {
            const cohortDate = new Date(cohort.cohortDate);
            const currentDate = new Date(dateStr);
            const daysSinceCohort = differenceInDays(currentDate, cohortDate);

            if (daysSinceCohort >= 0 && cohort.roasCurve.length > 0) {
              if (isSparseCurve) {
                // Кривая прорежена сервером до ключевых дней: между ними интерполируем.
                // Комиссии уже вшиты в cumulativeRevenue и линейны, так что
                // интерполяция итога равна итогу интерполированного breakdown.
                const revenue = interpolateCumulativeRevenue(cohort.roasCurve, daysSinceCohort);
                if (revenue !== undefined) {
                  totalCost += cohort.initialCost;
                  totalRevenue += revenue;
                }
                return;
              }

              let point = cohort.roasCurve.find(p => p.dayFromInstall === daysSinceCohort);

              if (!point) {
                // Если точной точки нет, берем последнюю доступную, но только если она раньше
                const lastPoint = cohort.roasCurve[cohort.roasCurve.length - 1];
                if (lastPoint.dayFromInstall <= daysSinceCohort) {
                  point = lastPoint;
                }
              }

              if (point) {
                totalCost += cohort.initialCost;
                totalRevenue += point.cumulativeRevenue;
              }
            }
          });
        
        const roas = totalCost > 0 ? (totalRevenue / totalCost) * 100 : 0;
        dataByDate[dateStr][campaign] = roas;
      });
    });

    // 4. Рассчитываем итоги по кампаниям
    const totals = {};
    campaignList.forEach(campaign => {
      const campaignCohorts = cohortsWithFees.filter(c => c.campaign === campaign);
      const totalCost = campaignCohorts.reduce((sum, c) => sum + c.initialCost, 0);
      
      const finalRevenue = campaignCohorts.reduce((sum, c) => {
        const lastPoint = c.roasCurve[c.roasCurve.length - 1];
        return sum + (lastPoint ? lastPoint.cumulativeRevenue : 0);
      }, 0);

      const finalRoas = totalCost > 0 ? (finalRevenue / totalCost) * 100 : 0;

      // Находим день окупаемости
      let breakevenDay = null;
      for (const dateStr of sortedDates) {
        const roas = dataByDate[dateStr][campaign];
        if (roas >= 100) {
          // Ищем первую когорту, чтобы посчитать дни от ее старта
          const firstCohortDate = campaignCohorts.reduce((earliest, c) => 
            new Date(c.cohortDate) < earliest ? new Date(c.cohortDate) : earliest, 
            new Date()
          );
          breakevenDay = differenceInDays(new Date(dateStr), firstCohortDate);
          break;
        }
      }

      totals[campaign] = {
        totalCost,
        totalRevenue: finalRevenue,
        finalRoas,
        breakevenDay,
      };
    });
    
    // 5. Рассчитываем CTR и CVR
    const summary = { ...rawData.summary };
    const totalClicks = cohortsWithFees.reduce((sum, c) => sum + (c.metadata.clicks || 0), 0);
    const totalImpressions = cohortsWithFees.reduce((sum, c) => sum + (c.metadata.impressions || 0), 0);
    const totalInstalls = cohortsWithFees.reduce((sum, c) => sum + c.cohortSize, 0);
    
    summary.ctr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    summary.cvr = totalClicks > 0 ? (totalInstalls / totalClicks) * 100 : 0;

    return {
      chartData: Object.values(dataByDate),
      totals,
      campaigns: campaignList,
      campaignMetadata,
      summary,
      cohortDateRange: rawData.summary?.dateRange,
      hiddenCampaignsCount
    };
  }, [rawData, shouldApplyFees, platform, minInstalls]);

  const { chartData, totals, campaigns, campaignMetadata, summary, cohortDateRange, hiddenCampaignsCount } = processedData;

  // Создаем маппинг цветов для кампаний
  const campaignColors = useMemo(() => {
    if (!campaigns) return {};
    const colors = {};
    campaigns.forEach((campaign, index) => {
      colors[campaign] = CAMPAIGN_COLORS[index % CAMPAIGN_COLORS.length];
    });
    return colors;
  }, [campaigns]);
  
  // Состояние видимости кампаний
  const [visibleCampaigns, setVisibleCampaigns] = useState({});

  // Потолок оси Y на графике ROAS (%), 0 = авто
  const [yAxisMax, setYAxisMax] = useState(() => readStoredNumber(ROAS_Y_MAX_STORAGE_KEY, DEFAULT_ROAS_Y_MAX));
  const handleYAxisMaxChange = (next) => {
    setYAxisMax(next);
    localStorage.setItem(ROAS_Y_MAX_STORAGE_KEY, String(next));
  };
  
  // Обновляем видимость кампаний при изменении данных
  React.useEffect(() => {
    if (campaigns && totals) {
      const initialVisibility = {};
      campaigns.forEach(campaign => {
        // По умолчанию все кампании скрыты — пользователь сам отмечает нужные
        initialVisibility[campaign] = false;
      });
      setVisibleCampaigns(initialVisibility);
    }
  }, [campaigns, totals]);
  
  // Обработчик переключения видимости кампании
  const handleToggleCampaign = (campaign, checked) => {
    setVisibleCampaigns(prev => ({
      ...prev,
      [campaign]: checked
    }));
  };
  
  // Вычисляем общую статистику
  const overallStats = useMemo(() => {
    if (!totals || Object.keys(totals).length === 0) return null;
    
    const totalsList = Object.values(totals);
    const totalCost = totalsList.reduce((sum, t) => sum + t.totalCost, 0);
    const totalRevenue = totalsList.reduce((sum, t) => sum + t.totalRevenue, 0);
    const overallRoas = totalCost > 0 ? (totalRevenue / totalCost) * 100 : 0;
    const breakevenCampaigns = totalsList.filter(t => t.breakevenDay !== null).length;
    
    return {
      totalCost,
      totalRevenue,
      overallRoas,
      breakevenCampaigns,
      totalCampaigns: totalsList.length,
      ctr: summary?.ctr || 0,
      cvr: summary?.cvr || 0,
    };
  }, [totals, summary]);
  
  return (
    <BaseWidget
      {...widgetProps}
      title="Cohort ROAS by Campaign"
      subtitle={cohortDateRange ? `Cohorts (install date): ${cohortDateRange.start} – ${cohortDateRange.end}` : null}
      emptyMessage="No ROAS data available for selected period"
      skeletonRows={8}
    >
      <div className="space-y-6">
        {/* Настройки: порог инсталлов действует на все цифры ниже, включая карточки */}
        <div className="flex flex-wrap items-center justify-end gap-4">
          {rawData?.curveResolution === 'keyDays' && (
            <span className="text-xs text-muted-foreground">
              Кривая по ключевым дням, между ними — интерполяция
            </span>
          )}
          {hiddenCampaignsCount > 0 && (
            <span className="text-xs text-amber-500">
              Скрыто кампаний с инсталлами &lt; {minInstalls}: {hiddenCampaignsCount}
            </span>
          )}
          <NumberSetting
            id="roas-min-installs"
            label="Мин. инсталлов"
            value={minInstalls}
            onChange={handleMinInstallsChange}
            step={50}
          />
          <NumberSetting
            id="roas-y-max"
            label="Макс. ось Y, % (0 = авто)"
            value={yAxisMax}
            onChange={handleYAxisMaxChange}
            step={50}
          />
        </div>

        {/* Общая статистика */}
        {overallStats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <StatCard
              icon={DollarSign}
              label="Общие затраты"
              value={MetricsService.formatCurrency(overallStats.totalCost)}
              subtitle={`${campaigns.length} кампаний`}
            />
            <StatCard
              icon={TrendingUp}
              label="Общий ROAS"
              value={`${overallStats.overallRoas.toFixed(2)}%`}
              subtitle={overallStats.overallRoas >= 100 ? 'Окупается' : 'Не окупается'}
              color={overallStats.overallRoas >= 100 ? 'green' : 'amber'}
            />
            <StatCard
              icon={Target}
              label="Окупаемость"
              value={`${overallStats.breakevenCampaigns}/${overallStats.totalCampaigns}`}
              subtitle="Кампаний окупилось"
            />
            <StatCard
              icon={MousePointerClick}
              label="CTR"
              value={`${overallStats.ctr.toFixed(2)}%`}
              subtitle="Clicks / Impressions"
            />
            <StatCard
              icon={Goal}
              label="CVR"
              value={`${overallStats.cvr.toFixed(2)}%`}
              subtitle="Installs / Clicks"
            />
             <StatCard
              icon={DollarSign}
              label="Общий доход"
              value={MetricsService.formatCurrency(overallStats.totalRevenue)}
              subtitle="Все кампании"
            />
          </div>
        )}
        
        {/* Предупреждение если нет данных */}
        {campaigns && campaigns.length === 0 && !widgetProps.isLoading && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Нет данных по кампаниям за выбранный период. Попробуйте изменить фильтры.
            </AlertDescription>
          </Alert>
        )}
        
        {/* График ROAS */}
        {chartData && chartData.length > 0 && campaigns && campaigns.length > 0 && (
          <div className="mt-6">
            <RoasChart
              yAxisMax={yAxisMax}
              chartData={chartData}
              campaigns={campaigns}
              visibleCampaigns={visibleCampaigns}
              totals={totals}
              campaignColors={campaignColors}
              campaignMetadata={campaignMetadata}
              cohortEndDate={dateRange?.endDate ? (() => {
                const d = new Date(dateRange.endDate);
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              })() : null}
            />
          </div>
        )}
        
        {/* Таблица итогов по кампаниям */}
        {campaigns && campaigns.length > 0 && (
          <div className="mt-6">
            <CampaignsTotalsTable
              campaigns={campaigns}
              totals={totals}
              visibleCampaigns={visibleCampaigns}
              onToggleCampaign={handleToggleCampaign}
              campaignColors={campaignColors}
              campaignMetadata={campaignMetadata}
            />
          </div>
        )}
      </div>
    </BaseWidget>
  );
}

export default RoasWidget;
